/**
 * «Подключения агентов» — task 46fcee9a, round-3 mockups approved by Alexander.
 *
 * Everything an agent connection may do is visible in one place: its scope,
 * the tasks where it may write, the secrets handed to it, its last action — and
 * each of them is taken back right there. Narrowing is immediate; widening needs
 * a new sign-in, and the server says so in its own words (422), shown as is.
 * A person grants rights only to their own connection on their own task
 * (PR #15); the task's «···» holds that form.
 */

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, KeyRound, Pencil } from "lucide-react";

import { AgentConnections, Delegations, Overview, Secrets } from "@/api/resources";
import type { ActivityOut, AgentConnectionOut, ConnectionDetailOut, DelegationOut } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { AppBar, ago, Caption, EmptyNote, genitive, ScreenHeader, Switch } from "@/kit";
import { activityLabel } from "@/lib/activity";
import { cn } from "@/lib/cn";
import { nameOf, usePeople } from "@/lib/people";
import { useBackButton } from "@/lib/telegram-ui";

const CONNECTIONS = ["agent-connections"] as const;
const DELEGATION_LABEL: Record<string, string> = { journal: "журнал", drafts: "черновик результата" };

/** «Агенты» for a computer's agents connection (Spec 027, D5), otherwise MCP or API. */
export function connectionKind(c: Pick<AgentConnectionOut, "kind" | "purpose">): string {
  if (c.purpose === "agents") return "Агенты";
  return c.kind === "mcp" ? "MCP" : "API";
}

/** The server keeps «MCP » in front of a sign-in connection's name; people need not see it. */
export function connectionName(c: Pick<AgentConnectionOut, "name" | "kind">): string {
  return c.kind === "mcp" && c.name.startsWith("MCP ") ? c.name.slice(4) : c.name;
}

export function scopeWords(scope: string[]): string {
  return scope.includes("write") ? "читать и писать" : "только читать";
}

function plural(n: number, one: string, few: string, many: string): string {
  const d = n % 10;
  const h = n % 100;
  if (d === 1 && h !== 11) return one;
  if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return few;
  return many;
}

function grantsLine(c: AgentConnectionOut): string {
  const tasks = c.task_grants_count ?? 0;
  const secrets = c.secret_grants_count ?? 0;
  // Spec 027, D6: a run's rights come from the agent's card, set by hand.
  if (c.purpose === "agents" && !secrets) return "права — по карточкам агентов";
  if (!tasks && !secrets) return "прав на задачи нет";
  const parts = [];
  if (tasks) parts.push(`${tasks} ${plural(tasks, "задача", "задачи", "задач")}`);
  if (secrets) parts.push(`${secrets} ${plural(secrets, "секрет", "секрета", "секретов")}`);
  return parts.join(" · ");
}

const day = (iso: string) => new Date(iso).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" });


/* ── The list ─────────────────────────────────────────────────────────── */

export function ConnectionsScreen() {
  useBackButton("/me");
  const [copied, setCopied] = useState("");
  const [showOff, setShowOff] = useState(false);
  const query = useQuery({ queryKey: CONNECTIONS, queryFn: AgentConnections.list });
  const data = query.data;
  const active = (data?.connections ?? []).filter((c) => !c.revoked_at);
  const off = (data?.connections ?? []).filter((c) => c.revoked_at);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data!.mcp_url);
      setCopied("Адрес скопирован.");
    } catch {
      setCopied("Выдели адрес и скопируй вручную.");
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <AppBar fallback="/me" context="Профиль" />
      <ScreenHeader title="Подключения агентов" sub="Агенты, которые работают от твоего имени" />
      {query.isLoading && <SkeletonList count={3} />}
      {query.isError && (
        <EmptyNote action={<Button size="compact" variant="secondary" onClick={() => void query.refetch()}>Повторить</Button>}>
          Не удалось загрузить подключения.
        </EmptyNote>
      )}
      {data && (
        <div className="flex flex-col gap-2 rounded-card border border-p-border bg-p-surface px-4 py-3.5">
          <span className="text-meta text-p-text-muted">Адрес для подключения агента</span>
          <code className="select-all break-all rounded-lg border border-p-border bg-p-surface-2 px-3 py-2 font-mono text-[13px] text-p-text">{data.mcp_url}</code>
          <div className="flex flex-wrap items-center gap-3">
            <Button size="compact" variant="secondary" onClick={() => void copy()}>Скопировать адрес</Button>
            <a href={data.connect_url} target="_blank" rel="noopener noreferrer" className="text-meta font-semibold text-p-link">Как подключить Codex и Claude</a>
          </div>
          <p className="text-meta text-p-text-muted" role="status">{copied || "Агент попросит войти — подтвердишь в Telegram."}</p>
        </div>
      )}
      {data?.agents_mcp_url && (
        <div className="flex flex-col gap-2 rounded-card border border-p-border bg-p-surface px-4 py-3.5">
          <span className="text-meta text-p-text-muted">Адрес подключения «Агенты» — для запуска агентов в терминале</span>
          <code className="select-all break-all rounded-lg border border-p-border bg-p-surface-2 px-3 py-2 font-mono text-[13px] text-p-text">{data.agents_mcp_url}</code>
          <p className="text-meta text-p-text-muted">
            Своё на каждом компьютере. Без запуска оно только читает карточки агентов; остальное — внутри запуска и по правам карточки.
          </p>
        </div>
      )}
      {data && active.length === 0 && <EmptyNote>Подключений пока нет. Они появятся, когда ты подтвердишь вход агента.</EmptyNote>}
      {active.length > 0 && (
        <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
          {active.map((c) => (
            <li key={c.id} className="border-b border-p-border last:border-0">
              <Link to={`/settings/agents/${c.id}`} className="flex items-start gap-3 px-4 py-3 press-surface">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-p-border bg-p-surface-2 text-p-text">
                  <KeyRound className="h-[18px] w-[18px]" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-semibold leading-5 text-p-text">{connectionName(c)}</span>
                  <span className="block text-meta text-p-text-muted">
                    {connectionKind(c)} · {scopeWords(c.scope)}{c.last_used_at ? ` · ${ago(c.last_used_at)}` : " · ещё не заходил"}
                  </span>
                  <span className="block text-meta text-p-text-muted">{grantsLine(c)}</span>
                </span>
                <ChevronRight className="mt-2.5 h-5 w-5 shrink-0 text-p-text-muted" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}
      {off.length > 0 && (
        <div className="text-meta text-p-text-muted">
          {showOff
            ? <ul className="flex flex-col gap-1">{off.map((c) => <li key={c.id}>«{connectionName(c)}» · отключено {day(c.revoked_at!)}</li>)}</ul>
            : <>Отключённые: {off.length} — <button type="button" className="font-semibold text-p-link" onClick={() => setShowOff(true)}>показать</button></>}
        </div>
      )}
    </div>
  );
}

/* ── One connection ───────────────────────────────────────────────────── */

export function ConnectionScreen() {
  useBackButton("/settings/agents");
  const { connection_id } = useParams<{ connection_id: string }>();
  const id = connection_id!;
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const people = usePeople();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [scopeError, setScopeError] = useState("");
  const [confirmOff, setConfirmOff] = useState(false);
  const detail = useQuery({ queryKey: ["agent-connection", id], queryFn: () => AgentConnections.get(id) });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["agent-connection", id] });
    void qc.invalidateQueries({ queryKey: CONNECTIONS });
  };

  const rename = useMutation({
    mutationFn: (next: string) => AgentConnections.update(id, { name: next }),
    onSuccess: () => { setEditing(false); refresh(); toast.success("Имя сохранено"); },
    onError: (e: Error) => toast.error(e.message),
  });
  const setScope = useMutation({
    mutationFn: (scope: ("read" | "write")[]) => AgentConnections.update(id, { scope }),
    onSuccess: () => { setScopeError(""); refresh(); },
    // Widening is refused by the server in its own words — shown as is.
    onError: (e: Error) => setScopeError(e.message),
  });
  const revokeTask = useMutation({
    mutationFn: (task_id: string) => Delegations.revoke(task_id, id),
    onSuccess: () => { refresh(); toast.success("Права на задачу отозваны"); },
    onError: (e: Error) => toast.error(e.message),
  });
  const revokeSecret = useMutation({
    mutationFn: (g: { secret_id: string; id: string; version: number }) => Secrets.revoke(g.secret_id, g.id, g.version),
    onSuccess: () => { refresh(); toast.success("Секрет отозван"); },
    onError: (e: Error) => toast.error(e.message),
  });
  const disconnect = useMutation({
    mutationFn: () => AgentConnections.disconnect(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CONNECTIONS });
      // Disconnecting takes the connection's task grants with it (PC audit 28.09, point 5).
      void qc.invalidateQueries({ queryKey: ["delegations"] });
      toast.success("Подключение отключено");
      navigate("/settings/agents", { replace: true });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const c: ConnectionDetailOut | undefined = detail.data;
  const writes = c?.scope.includes("write") ?? false;
  const live = c && !c.revoked_at;
  const secrets = (c?.secret_grants ?? []).filter((g) => g.active);

  return (
    <div className="flex flex-col gap-3">
      <AppBar fallback="/settings/agents" context="Подключения агентов" />
      {detail.isLoading && <SkeletonList count={4} />}
      {detail.isError && <EmptyNote>Подключение не найдено.</EmptyNote>}
      {c && (
        <>
          {editing ? (
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => { e.preventDefault(); if (name.trim()) rename.mutate(name.trim()); }}
            >
              <input
                autoFocus
                value={name}
                maxLength={60}
                onChange={(e) => setName(e.target.value)}
                aria-label="Имя подключения"
                className="h-12 min-w-0 flex-1 rounded-xl border border-p-border-strong bg-p-surface px-3 text-[17px] font-semibold text-p-text outline-none focus:outline focus:outline-2 focus:outline-p-focus"
              />
              <Button size="compact" variant="strong" loading={rename.isPending} disabled={!name.trim()} type="submit">Сохранить</Button>
            </form>
          ) : (
            <button
              type="button"
              disabled={!live}
              onClick={() => { setName(connectionName(c)); setEditing(true); }}
              className="flex h-12 items-center justify-between gap-2 rounded-xl border border-p-border-strong bg-p-surface px-3 text-left text-[17px] font-semibold text-p-text press-surface disabled:opacity-70"
              aria-label="Переименовать подключение"
            >
              <span className="min-w-0 truncate">{connectionName(c)}</span>
              {live && <Pencil className="h-4 w-4 shrink-0 text-p-text-muted" aria-hidden="true" />}
            </button>
          )}
          <p className="text-meta text-p-text-muted">
            {c.kind === "mcp" ? "MCP" : "API"} · подключено {day(c.created_at)}
            {c.last_used_at ? ` · последнее обращение ${ago(c.last_used_at)}` : " · ещё не заходил"}
            {c.revoked_at ? ` · отключено ${day(c.revoked_at)}` : ""}
          </p>
          {c.last_action && (
            <p className="flex gap-2 rounded-xl bg-p-now-tint px-3 py-2.5 text-body text-p-text">
              <span aria-hidden="true" className="mt-[7px] h-2 w-2 shrink-0 rounded-full bg-p-text" />
              <span>
                <b className="font-bold">Последнее:</b>{" "}
                {activityLabel({ type: c.last_action.type, entity_type: c.last_action.entity_type } as ActivityOut)}
                {c.last_action.task_id && c.last_action.task_title && (
                  <> · <Link to={`/tasks/${c.last_action.task_id}`} className="text-p-link">«{c.last_action.task_title}»</Link></>
                )}
                {" · "}{ago(c.last_action.at)}
              </span>
            </p>
          )}

          {live && (
            <>
              <Caption>Может</Caption>
              <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
                <li className="flex items-center gap-3 border-b border-p-border px-4 py-3">
                  <span className="min-w-0 flex-1"><span className="block text-[15px] font-semibold text-p-text">Читать</span>
                    <span className="block text-meta text-p-text-muted">задачи, идеи, обсуждения, знания</span></span>
                  <span className="shrink-0 text-meta text-p-text-muted">всегда</span>
                </li>
                <li className="flex items-center gap-3 px-4 py-3">
                  <span className="min-w-0 flex-1"><span className="block text-[15px] font-semibold text-p-text">Писать</span>
                    <span className="block text-meta text-p-text-muted">сообщения, журнал, результаты, идеи</span></span>
                  <Switch on={writes} disabled={setScope.isPending} label="Писать"
                    onChange={() => setScope.mutate(writes ? ["read"] : ["read", "write"])} />
                </li>
              </ul>
              <p role={scopeError ? "alert" : undefined} className={cn("text-meta", scopeError ? "text-p-blocked" : "text-p-text-muted")}>
                {scopeError || "«Писать» выключается сразу, включается снова только новым входом агента. Чтение выключает только «Отключить подключение»."}
              </p>
            </>
          )}

          <Caption>Задачи · {c.task_grants.length}</Caption>
          {c.task_grants.length === 0
            ? <p className="text-meta text-p-text-muted">{live && writes ? "Прав на задачи пока нет." : "Прав на задачи нет."}</p>
            : (
              <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
                {c.task_grants.map((g) => (
                  <li key={g.id} className="flex items-center gap-3 border-b border-p-border px-4 py-3 last:border-0">
                    <Link to={`/tasks/${g.task_id}`} className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-semibold text-p-text">{g.task_title}</span>
                      <span className="block truncate text-meta text-p-text-muted">
                        {[g.project_title, g.scope.map((s) => DELEGATION_LABEL[s] ?? s).join(", "), `выдал ${g.granted_by_name ?? nameOf(people.data, g.granted_by) ?? "участник"}`].filter(Boolean).join(" · ")}
                      </span>
                    </Link>
                    {live && <Button size="compact" variant="ghost" loading={revokeTask.isPending && revokeTask.variables === g.task_id}
                      onClick={() => revokeTask.mutate(g.task_id)}>Отозвать</Button>}
                  </li>
                ))}
              </ul>
            )}

          {live && writes && <GrantTaskFromConnection tokenId={c.id} granted={c.task_grants.map((g) => g.task_id)} />}

          <Caption>Секреты · {secrets.length}</Caption>
          {secrets.length === 0
            ? <p className="text-meta text-p-text-muted">Секретов этому подключению не выдано.</p>
            : (
              <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
                {secrets.map((g) => (
                  <li key={g.id} className="flex items-center gap-3 border-b border-p-border px-4 py-3 last:border-0">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-semibold text-p-text">{g.secret_name}</span>
                      <span className="block truncate text-meta text-p-text-muted">
                        {[g.resource_name, g.expires_at ? `до ${day(g.expires_at)}` : null, g.granted_by_name ? `выдал ${g.granted_by_name}` : null].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    {live && <Button size="compact" variant="ghost" loading={revokeSecret.isPending && revokeSecret.variables?.id === g.id}
                      onClick={() => revokeSecret.mutate(g)}>Отозвать</Button>}
                  </li>
                ))}
              </ul>
            )}

          {live && (
            <div className="mt-2 flex flex-col gap-2">
              {confirmOff && <p className="text-meta text-p-text-muted">Агент потеряет доступ сразу: права на задачи и секреты перестанут действовать. Вернуть — только новым входом.</p>}
              <Button variant="destructive" block loading={disconnect.isPending}
                onClick={() => (confirmOff ? disconnect.mutate() : setConfirmOff(true))}>
                {confirmOff ? "Точно отключить" : "Отключить подключение"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── A task's «···» → «Доступ агента» ─────────────────────────────────── */

/**
 * A person lets their own agent work in their own task: keep the journal and/or
 * prepare the result for their check (PR #15 rules; the server refuses the
 * rest). What is granted is listed below with «Отозвать».
 */
export function TaskAgentAccess({ taskId, canGrant }: { taskId: string; canGrant: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const people = usePeople();
  const connections = useQuery({ queryKey: CONNECTIONS, queryFn: AgentConnections.list });
  // With revoked rows: the grants of a disconnected connection fold under «показать».
  const grants = useQuery({ queryKey: ["delegations", taskId], queryFn: () => Delegations.list(taskId, true) });
  const [showOff, setShowOff] = useState(false);
  const all = connections.data?.connections ?? [];
  const mine = all.filter((c) => !c.revoked_at);
  const [chosen, setChosen] = useState<string | null>(null);
  const [scope, setScope] = useState<string[]>(["journal"]);
  const pick = chosen ?? mine.find((c) => c.scope.includes("write"))?.id ?? null;
  const done = () => {
    void qc.invalidateQueries({ queryKey: ["delegations", taskId] });
    void qc.invalidateQueries({ queryKey: CONNECTIONS });
  };
  const grant = useMutation({
    mutationFn: () => Delegations.grant(taskId, pick!, scope),
    onSuccess: () => { done(); toast.success("Доступ выдан"); },
    onError: (e: Error) => toast.error(e.message),
  });
  const revoke = useMutation({
    mutationFn: (token_id: string) => Delegations.revoke(taskId, token_id),
    onSuccess: () => { done(); toast.success("Доступ отозван"); },
    onError: (e: Error) => toast.error(e.message),
  });
  // A grant of a disconnected connection does not work even if an old row still looks live.
  const live = (grants.data ?? []).filter((d) => !d.revoked_at && !d.token_revoked);
  const off = (grants.data ?? []).filter((d) => d.token_revoked);
  const toggle = (s: string) => setScope((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  // A person grants only to their own connection, so whoever granted owns the agent.
  const grantee = (d: DelegationOut) => {
    const own = all.find((c) => c.id === d.token_id);
    if (own) return connectionName(own);
    if (d.token_name) return connectionName({ name: d.token_name, kind: d.token_name.startsWith("MCP ") ? "mcp" : "api" });
    return `агент ${genitive(nameOf(people.data, d.granted_by) ?? "участника")}`;
  };

  return (
    <section className="space-y-2" aria-label="Доступ агента">
      <Caption>Доступ агента</Caption>
      {canGrant ? (
        <>
          <p className="text-meta text-p-text-muted">Твой агент сможет работать в этой задаче. Выдать можно только своему агенту и только в своей задаче.</p>
          {connections.isLoading && <SkeletonList count={1} />}
          {connections.data && mine.length === 0 && (
            <p className="text-meta text-p-text-muted">Подключений нет. <Link to="/settings/agents" className="font-semibold text-p-link">Подключить агента</Link></p>
          )}
          {mine.length > 0 && (
            <div role="radiogroup" aria-label="Какому агенту" className="overflow-hidden rounded-card border border-p-border bg-p-surface">
              {mine.map((c) => {
                const writes = c.scope.includes("write");
                const on = pick === c.id;
                return (
                  <button key={c.id} type="button" role="radio" aria-checked={on} disabled={!writes}
                    onClick={() => setChosen(c.id)}
                    className="flex w-full items-center gap-3 border-b border-p-border px-4 py-3 text-left last:border-0 press-surface disabled:opacity-50">
                    <span className={cn("h-5 w-5 shrink-0 rounded-full border-2", on ? "border-[6px] border-p-text" : "border-p-border-strong")} aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-semibold text-p-text">{connectionName(c)}</span>
                      <span className="block text-meta text-p-text-muted">{writes ? `${c.kind === "mcp" ? "MCP" : "API"} · читать и писать` : "только читать — писать в задачу не сможет"}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {mine.length > 0 && (
            <>
              {(["journal", "drafts"] as const).map((s) => (
                <label key={s} className="flex min-h-11 cursor-pointer items-start gap-3">
                  <input type="checkbox" className="mt-1 h-5 w-5 accent-[var(--p-text)]" checked={scope.includes(s)} onChange={() => toggle(s)} />
                  <span>
                    <span className="block text-[15px] font-semibold text-p-text">{s === "journal" ? "Вести журнал" : "Готовить результат"}</span>
                    <span className="block text-meta text-p-text-muted">{s === "journal" ? "этап, следующий шаг, препятствия" : "черновик для твоей проверки — подтверждаешь ты"}</span>
                  </span>
                </label>
              ))}
              <Button block variant="strong" disabled={!pick || scope.length === 0} loading={grant.isPending} onClick={() => grant.mutate()}>Выдать доступ</Button>
            </>
          )}
        </>
      ) : (
        <p className="text-meta text-p-text-muted">Выдать доступ агенту может только исполнитель задачи. Возьми задачу себе выше — тогда сможешь выдать доступ своему агенту.</p>
      )}
      {live.length > 0 && (
        <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
          {live.map((d) => (
            <li key={d.id} className="flex items-center gap-3 border-b border-p-border px-4 py-3 last:border-0">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] font-semibold text-p-text">{grantee(d)}</span>
                <span className="block text-meta text-p-text-muted">{d.scope.map((s) => DELEGATION_LABEL[s] ?? s).join(", ")} · выдал {nameOf(people.data, d.granted_by) ?? "участник"}</span>
              </span>
              <Button size="compact" variant="ghost" loading={revoke.isPending && revoke.variables === d.token_id} onClick={() => revoke.mutate(d.token_id)}>Отозвать</Button>
            </li>
          ))}
        </ul>
      )}
      {canGrant && live.length === 0 && grants.data && <p className="text-meta text-p-text-muted">Пока никому не выдано.</p>}
      {off.length > 0 && (
        <>
          <button type="button" aria-expanded={showOff} onClick={() => setShowOff((v) => !v)}
            className="min-h-11 text-left text-meta font-semibold text-p-link">
            {showOff ? "Скрыть" : "Показать"} доступы отключённых подключений ({off.length})
          </button>
          {showOff && (
            <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
              {off.map((d) => (
                <li key={d.id} className="border-b border-p-border px-4 py-3 last:border-0">
                  <span className="block truncate text-[15px] font-semibold text-p-text-muted">{grantee(d)} · отключено</span>
                  <span className="block text-meta text-p-text-muted">{d.scope.map((s) => DELEGATION_LABEL[s] ?? s).join(", ")} · больше не действует</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

/**
 * Give this connection access to one of my open tasks from the connection
 * itself (PC audit 28.09, point 3): until now it could only be done from inside
 * the task, under «···», and on a computer that read as «impossible».
 */
function GrantTaskFromConnection({ tokenId, granted }: { tokenId: string; granted: string[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [scope, setScope] = useState<string[]>(["journal"]);
  const overview = useQuery({ queryKey: ["overview"], queryFn: Overview.get, staleTime: 60_000, enabled: open });
  const me = overview.data?.me.id;
  const tasks = (overview.data?.active ?? []).filter((a) => a.assignee?.id === me && !granted.includes(a.item.id));
  const grant = useMutation({
    mutationFn: () => Delegations.grant(taskId!, tokenId, scope),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CONNECTIONS });
      void qc.invalidateQueries({ queryKey: ["agent-connection", tokenId] });
      void qc.invalidateQueries({ queryKey: ["delegations", taskId] });
      toast.success("Доступ выдан");
      setOpen(false);
      setTaskId(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const toggle = (s: string) => setScope((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  if (!open) {
    return <Button variant="secondary" block onClick={() => setOpen(true)}>Выдать доступ к задаче</Button>;
  }
  return (
    <section className="space-y-2" aria-label="Выдать доступ к задаче">
      <p className="text-meta text-p-text-muted">Твои открытые задачи. Выдать можно только в своей задаче.</p>
      {overview.isLoading && <SkeletonList count={2} />}
      {overview.data && tasks.length === 0 && <EmptyNote>Своих открытых задач без этого доступа нет.</EmptyNote>}
      {tasks.length > 0 && (
        <div role="radiogroup" aria-label="Какая задача" className="max-h-[320px] overflow-y-auto rounded-card border border-p-border bg-p-surface">
          {tasks.map((a) => {
            const on = taskId === a.item.id;
            return (
              <button key={a.item.id} type="button" role="radio" aria-checked={on} onClick={() => setTaskId(a.item.id)}
                className="flex w-full items-center gap-3 border-b border-p-border px-4 py-3 text-left last:border-0 press-surface">
                <span className={cn("h-5 w-5 shrink-0 rounded-full border-2", on ? "border-[6px] border-p-text" : "border-p-border-strong")} aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-semibold leading-5 text-p-text [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden">{a.item.title}</span>
                  {a.item.project && <span className="block truncate text-meta text-p-text-muted">{a.item.project}</span>}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {tasks.length > 0 && (["journal", "drafts"] as const).map((s) => (
        <label key={s} className="flex min-h-11 cursor-pointer items-start gap-3">
          <input type="checkbox" className="mt-1 h-5 w-5 accent-[var(--p-text)]" checked={scope.includes(s)} onChange={() => toggle(s)} />
          <span>
            <span className="block text-[15px] font-semibold text-p-text">{s === "journal" ? "Вести журнал" : "Готовить результат"}</span>
            <span className="block text-meta text-p-text-muted">{s === "journal" ? "этап, следующий шаг, препятствия" : "черновик для твоей проверки — подтверждаешь ты"}</span>
          </span>
        </label>
      ))}
      <div className="flex gap-2">
        <Button variant="strong" disabled={!taskId || scope.length === 0} loading={grant.isPending} onClick={() => grant.mutate()}>Выдать доступ</Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>Отмена</Button>
      </div>
    </section>
  );
}
